import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { API_ENDPOINTS } from './constants';
import { Header } from './components/ui';
import { tokens } from './styles/tokens';

function ProfilePage() {
  const navigate = useNavigate();
  const { 
    isAuthenticated, 
    loading, 
    logout, 
    getUserAttributes,
    getAccessToken,
    userInfo
  } = useAuth();
  const [error, setError] = useState('');
  const [bets, setBets] = useState([]);
  const [loadingBets, setLoadingBets] = useState(false);
  const [betsError, setBetsError] = useState('');
  const [activeTab, setActiveTab] = useState('profile');



  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const fetchUserBets = async () => {
    try {
      setLoadingBets(true);
      setBetsError('');
      const accessToken = getAccessToken();
      
      if (!accessToken) {
        throw new Error('No access token available');
      }

      const response = await fetch(`${API_ENDPOINTS.USER_API}/bet`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBets(data.bets || []);
      } else if (response.status === 404) {
        setBets([]);
      } else {
        throw new Error(`Failed to load bets: ${response.status}`);
      }
      
    } catch (error) {
      console.error('Error fetching user bets:', error);
      setBetsError(error.message);
    } finally {
      setLoadingBets(false);
    }
  };

  // Fetch bets data when component mounts for authenticated users
  useEffect(() => {
    if (isAuthenticated) {
      fetchUserBets();
    }
  }, [isAuthenticated]);

  // Additional fetch when switching to pending bets tab (for refresh functionality)
  useEffect(() => {
    if (isAuthenticated && activeTab === 'pending bets') {
      fetchUserBets();
    }
  }, [activeTab]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: tokens.colors.gradients.dark,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: `3px solid ${tokens.colors.neutral[800]}`,
            borderTopColor: tokens.colors.accent[400],
            borderRadius: '50%',
            margin: '0 auto',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ color: tokens.colors.neutral[400], marginTop: tokens.spacing[4] }}>
            Loading profile...
          </p>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }
  
  if (!isAuthenticated) return null;

  const StatCard = ({ label, value, icon, trend }) => (
    <div style={{
      background: `linear-gradient(135deg, ${tokens.colors.dark[800]}99 0%, ${tokens.colors.dark[900]}99 100%)`,
      backdropFilter: 'blur(10px)',
      border: `1px solid ${tokens.colors.neutral[800]}`,
      borderRadius: tokens.borderRadius.xl,
      padding: tokens.spacing[6],
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div style={{
        position: 'absolute',
        top: '-20px',
        right: '-20px',
        fontSize: '80px',
        color: tokens.colors.accent[900],
        opacity: 0.1
      }}>
        {icon}
      </div>
      <div style={{ position: 'relative', zIndex: 1 }}>
        <p style={{
          color: tokens.colors.neutral[400],
          fontSize: tokens.typography.fontSize.sm,
          marginBottom: tokens.spacing[2]
        }}>
          {label}
        </p>
        <p style={{
          color: tokens.colors.neutral[0],
          fontSize: tokens.typography.fontSize['2xl'],
          fontWeight: tokens.typography.fontWeight.bold
        }}>
          {value}
        </p>
        {trend && (
          <p style={{
            color: trend > 0 ? tokens.colors.accent[400] : tokens.colors.error[400],
            fontSize: tokens.typography.fontSize.sm,
            marginTop: tokens.spacing[1]
          }}>
            {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </p>
        )}
      </div>
    </div>
  );

  const menuItems = [
    { label: 'Home', href: '/home' },
    { label: 'Sports', href: '/sports' },
    { label: 'My Bets', href: '/my-bets' }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: tokens.colors.gradients.dark,
      position: 'relative'
    }}>
      <Header
        logo="ELXR Sportsbook"
        user={userInfo ? {
          name: userInfo.given_name || userInfo.name,
          email: userInfo.email
        } : null}
        onLogout={handleLogout}
        menuItems={menuItems}
      />
      
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: `radial-gradient(circle at 10% 20%, ${tokens.colors.accent[900]}11 0%, transparent 50%),
                          radial-gradient(circle at 80% 80%, ${tokens.colors.primary[900]}11 0%, transparent 50%)`,
        pointerEvents: 'none'
      }} />

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: `${tokens.spacing[8]} ${tokens.spacing[6]}`,
        position: 'relative',
        zIndex: 1
      }}>
        {/* Profile Header */}
        <div style={{
          background: `linear-gradient(135deg, ${tokens.colors.dark[800]}99 0%, ${tokens.colors.dark[900]}99 100%)`,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${tokens.colors.neutral[800]}`,
          borderRadius: tokens.borderRadius['2xl'],
          padding: tokens.spacing[8],
          marginBottom: tokens.spacing[8],
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative gradient */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '120px',
            background: tokens.colors.gradients.glow,
            opacity: 0.1
          }} />

          <div style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: tokens.spacing[4]
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[6] }}>
              {/* Avatar */}
              <div style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: tokens.colors.gradients.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: tokens.typography.fontSize['3xl'],
                fontWeight: tokens.typography.fontWeight.bold,
                color: tokens.colors.dark[900],
                boxShadow: `0 0 0 4px ${tokens.colors.dark[800]}, 0 0 0 8px ${tokens.colors.accent[700]}33`
              }}>
                {userInfo?.given_name?.[0] || userInfo?.email?.[0] || 'U'}
              </div>

              <div>
                <h1 style={{
                  fontSize: tokens.typography.fontSize['3xl'],
                  fontWeight: tokens.typography.fontWeight.bold,
                  color: tokens.colors.neutral[0],
                  marginBottom: tokens.spacing[1]
                }}>
                  {userInfo?.given_name || 'User'} {userInfo?.family_name || ''}
                </h1>
                <p style={{
                  color: tokens.colors.neutral[400],
                  fontSize: tokens.typography.fontSize.lg
                }}>
                  {userInfo?.email || 'No email'}
                </p>
              </div>
            </div>

            <button
              onClick={handleLogout}
              style={{
                background: `${tokens.colors.error[900]}33`,
                border: `1px solid ${tokens.colors.error[700]}`,
                color: tokens.colors.error[400],
                padding: `${tokens.spacing[3]} ${tokens.spacing[6]}`,
                borderRadius: tokens.borderRadius.lg,
                fontWeight: tokens.typography.fontWeight.semibold,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = tokens.colors.error[900];
                e.target.style.color = tokens.colors.neutral[0];
              }}
              onMouseLeave={(e) => {
                e.target.style.background = `${tokens.colors.error[900]}33`;
                e.target.style.color = tokens.colors.error[400];
              }}
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: tokens.spacing[6],
          marginBottom: tokens.spacing[8]
        }}>
          <StatCard 
            label="Total Bets" 
            value={bets.length} 
            icon="🎲"
          />
          <StatCard 
            label="Win Rate" 
            value={(() => {
              const wonBets = bets.filter(b => b.status === 'won').length;
              const lostBets = bets.filter(b => b.status === 'lost').length;
              const completedBets = wonBets + lostBets;
              return completedBets > 0 ? 
                `${Math.round((wonBets / completedBets) * 100)}%` : 
                'N/A';
            })()}
            icon="🏆"
            trend={bets.length > 0 ? 12 : null}
          />
          <StatCard 
            label="Total Volume" 
            value={bets.length > 0 ? 
              `${bets.reduce((sum, bet) => sum + parseFloat(bet.amount || 0), 0).toFixed(2)} XRP` : 
              '0 XRP'
            } 
            icon="💰"
          />

        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: tokens.spacing[2],
          marginBottom: tokens.spacing[6],
          borderBottom: `1px solid ${tokens.colors.neutral[800]}`,
          paddingBottom: tokens.spacing[4]
        }}>
          {['profile', 'pending bets', 'settings'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                background: 'none',
                border: 'none',
                color: activeTab === tab ? tokens.colors.accent[400] : tokens.colors.neutral[500],
                padding: `${tokens.spacing[3]} ${tokens.spacing[6]}`,
                fontWeight: tokens.typography.fontWeight.semibold,
                fontSize: tokens.typography.fontSize.base,
                cursor: 'pointer',
                position: 'relative',
                transition: 'color 0.2s',
                textTransform: 'capitalize'
              }}
            >
              {tab}
              {activeTab === tab && (
                <div style={{
                  position: 'absolute',
                  bottom: `-${tokens.spacing[4]}`,
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: tokens.colors.accent[400]
                }} />
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{
          background: `linear-gradient(135deg, ${tokens.colors.dark[800]}99 0%, ${tokens.colors.dark[900]}99 100%)`,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${tokens.colors.neutral[800]}`,
          borderRadius: tokens.borderRadius.xl,
          padding: tokens.spacing[8],
          minHeight: '400px'
        }}>
          {activeTab === 'profile' && (
            <div>
              <h2 style={{
                fontSize: tokens.typography.fontSize['2xl'],
                fontWeight: tokens.typography.fontWeight.bold,
                color: tokens.colors.neutral[0],
                marginBottom: tokens.spacing[6]
              }}>
                Account Information
              </h2>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: tokens.spacing[6]
              }}>
                {[
                  { label: 'Email Address', value: userInfo?.email || 'Not set', icon: '📧' },
                  { label: 'First Name', value: userInfo?.given_name || 'Not set', icon: '👤' },
                  { label: 'Last Name', value: userInfo?.family_name || 'Not set', icon: '👤' },
                  { label: 'Wallet Address', value: userInfo?.['custom:wallet_address'] || 'Not set', icon: '💳' },
                  { label: 'Date of Birth', value: formatDate(userInfo?.birthdate), icon: '🎂' },
                  { label: 'Account Status', value: 'Active', icon: '✅' }
                ].map((field, index) => (
                  <div key={index} style={{
                    padding: tokens.spacing[4],
                    background: `${tokens.colors.dark[700]}44`,
                    borderRadius: tokens.borderRadius.lg,
                    border: `1px solid ${tokens.colors.neutral[800]}`
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: tokens.spacing[3],
                      marginBottom: tokens.spacing[2]
                    }}>
                      <span style={{ fontSize: '1.5rem' }}>{field.icon}</span>
                      <span style={{
                        color: tokens.colors.neutral[400],
                        fontSize: tokens.typography.fontSize.sm
                      }}>
                        {field.label}
                      </span>
                    </div>
                    <p style={{
                      color: tokens.colors.neutral[0],
                      fontSize: tokens.typography.fontSize.base,
                      fontFamily: field.label === 'Wallet Address' ? tokens.typography.fontFamily.mono : 'inherit',
                      wordBreak: 'break-all'
                    }}>
                      {field.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'pending bets' && (
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: tokens.spacing[6]
              }}>
                <h2 style={{
                  fontSize: tokens.typography.fontSize['2xl'],
                  fontWeight: tokens.typography.fontWeight.bold,
                  color: tokens.colors.neutral[0]
                }}>
                  Pending Bets
                </h2>
                <button
                  onClick={fetchUserBets}
                  disabled={loadingBets}
                  style={{
                    background: tokens.colors.gradients.accent,
                    border: 'none',
                    color: tokens.colors.dark[900],
                    padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
                    borderRadius: tokens.borderRadius.lg,
                    fontWeight: tokens.typography.fontWeight.semibold,
                    cursor: loadingBets ? 'not-allowed' : 'pointer',
                    opacity: loadingBets ? 0.6 : 1
                  }}
                >
                  {loadingBets ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>

              {loadingBets ? (
                <div style={{ textAlign: 'center', padding: tokens.spacing[12] }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: `3px solid ${tokens.colors.neutral[800]}`,
                    borderTopColor: tokens.colors.accent[400],
                    borderRadius: '50%',
                    margin: '0 auto',
                    animation: 'spin 1s linear infinite'
                  }} />
                </div>
              ) : betsError ? (
                <div style={{
                  background: `${tokens.colors.error[900]}33`,
                  border: `1px solid ${tokens.colors.error[700]}`,
                  borderRadius: tokens.borderRadius.lg,
                  padding: tokens.spacing[6],
                  textAlign: 'center',
                  color: tokens.colors.error[400]
                }}>
                  {betsError}
                </div>
              ) : bets.filter(bet => !bet.status || bet.status === 'pending').length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: tokens.spacing[12]
                }}>
                  <div style={{
                    fontSize: '4rem',
                    marginBottom: tokens.spacing[4],
                    opacity: 0.2
                  }}>
                    🎲
                  </div>
                  <p style={{
                    color: tokens.colors.neutral[400],
                    fontSize: tokens.typography.fontSize.lg,
                    marginBottom: tokens.spacing[6]
                  }}>
                    No pending bets
                  </p>
                  <button
                    onClick={() => navigate('/home')}
                    style={{
                      background: tokens.colors.gradients.accent,
                      border: 'none',
                      color: tokens.colors.dark[900],
                      padding: `${tokens.spacing[3]} ${tokens.spacing[6]}`,
                      borderRadius: tokens.borderRadius.lg,
                      fontWeight: tokens.typography.fontWeight.semibold,
                      cursor: 'pointer'
                    }}
                  >
                    Start Betting
                  </button>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gap: tokens.spacing[4]
                }}>
                  {bets.filter(bet => !bet.status || bet.status === 'pending').map((bet, index) => (
                    <div 
                      key={index} 
                      onClick={() => navigate(`/current_bet/${bet.id || bet._id || index}`, { state: { bet } })}
                      style={{
                        background: '#fbbf2433', // Yellow background for pending bets
                        border: `1px solid #fbbf24`,
                        borderRadius: tokens.borderRadius.lg,
                        padding: tokens.spacing[6],
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: tokens.spacing[4],
                        alignItems: 'center',
                        transition: 'all 0.2s',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#fbbf24';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#fbbf24';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}>
                      <div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: tokens.spacing[3],
                          marginBottom: tokens.spacing[2]
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: tokens.spacing[2],
                            padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
                            background: (() => {
                              const status = (bet.status || 'pending').toLowerCase();
                              switch(status) {
                                case 'won': return `${tokens.colors.accent[900]}33`;
                                case 'lost': return `${tokens.colors.error[900]}33`;
                                case 'refunded': return '#7c3aed33'; // purple background
                                default: return '#fbbf2433'; // yellow background for pending
                              }
                            })(),
                            border: `1px solid ${(() => {
                              const status = (bet.status || 'pending').toLowerCase();
                              switch(status) {
                                case 'won': return tokens.colors.accent[700];
                                case 'lost': return tokens.colors.error[700];
                                case 'refunded': return '#7c3aed'; // purple border
                                default: return '#fbbf24'; // yellow border for pending
                              }
                            })()}`,
                            borderRadius: tokens.borderRadius.full
                          }}>
                            <div style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: (() => {
                                const status = (bet.status || 'pending').toLowerCase();
                                switch(status) {
                                  case 'won': return '#22c55e'; // green
                                  case 'lost': return '#ef4444'; // red
                                  case 'refunded': return '#7c3aed'; // purple
                                  default: return '#fbbf24'; // yellow for pending
                                }
                              })()
                            }} />
                            <span style={{
                              color: (() => {
                                const status = (bet.status || 'pending').toLowerCase();
                                switch(status) {
                                  case 'won': return tokens.colors.accent[400];
                                  case 'lost': return tokens.colors.error[400];
                                  case 'refunded': return '#a855f7'; // purple text
                                  default: return '#fbbf24'; // yellow text for pending
                                }
                              })(),
                              fontSize: tokens.typography.fontSize.xs,
                              fontWeight: tokens.typography.fontWeight.semibold,
                              textTransform: 'uppercase'
                            }}>
                              {bet.status || 'Pending'}
                            </span>
                          </div>
                          <span style={{
                            color: tokens.colors.neutral[500],
                            fontSize: tokens.typography.fontSize.sm
                          }}>
                            {bet.created_at ? new Date(bet.created_at).toLocaleDateString() : 'Date unknown'}
                          </span>
                        </div>
                        <p style={{
                          color: tokens.colors.neutral[0],
                          fontSize: tokens.typography.fontSize.base,
                          marginBottom: tokens.spacing[1]
                        }}>
                          {bet.outcome || bet.prediction || 'Unknown prediction'}
                        </p>
                        <p style={{
                          color: tokens.colors.neutral[400],
                          fontSize: tokens.typography.fontSize.sm
                        }}>
                          {bet.homeTeam && bet.awayTeam ? 
                            `${bet.homeTeam} vs ${bet.awayTeam}` : 
                            `Game ID: ${bet.gameId || bet.game_id || 'N/A'}`
                          }
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{
                          color: tokens.colors.accent[400],
                          fontSize: tokens.typography.fontSize.xl,
                          fontWeight: tokens.typography.fontWeight.bold
                        }}>
                          {bet.amount || '0'} XRP
                        </p>
                        {bet.payout && (
                          <p style={{
                            color: tokens.colors.neutral[500],
                            fontSize: tokens.typography.fontSize.sm
                          }}>
                            Payout: {bet.payout} XRP
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div style={{
              textAlign: 'center',
              padding: tokens.spacing[12]
            }}>
              <div style={{
                fontSize: '4rem',
                marginBottom: tokens.spacing[4],
                opacity: 0.2
              }}>
                ⚙️
              </div>
              <p style={{
                color: tokens.colors.neutral[400],
                fontSize: tokens.typography.fontSize.lg
              }}>
                Settings coming soon
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfilePage; 